"use client";

import { cn } from "@ui/lib/utils";
import type { ItemBase } from "@components/core/ListBox";

import { BaseLink } from "@components/core/Link";
import { SearchComboBox, type SearchComboBoxProps } from "@components/core/SearchComboBox";

interface SearchSuggestion {
  id: string;
  label: string;
  /** Accessible label for the chip button. Provide a translated string when used in a localised context. Falls back to `label`. */
  ariaLabel?: string;
  onSelect?: (newTab: boolean) => void;
  href?: string;
}

interface SearchInputProps<Item extends ItemBase> extends SearchComboBoxProps<Item> {
  suggestions?: SearchSuggestion[];
  suggestionsLabel?: string;
}

function SearchInput<Item extends ItemBase>({
  suggestions,
  suggestionsLabel,
  className,
  ...comboBoxProps
}: SearchInputProps<Item>) {
  return (
    <div role='search' className={cn("tw-w-full", className)}>
      <SearchComboBox<Item> {...comboBoxProps} />

      {suggestions && suggestions.length > 0 && (
        <div className='tw-mt-4 tw-flex tw-items-center tw-gap-4'>
          {suggestionsLabel && <span className='tw-shrink-0 tw-type-sm tw-text-gray-500'>{suggestionsLabel}</span>}
          {/* horizontal scroll on overflow, no wrapping */}
          <div
            className='tw-scrollbar-none tw-min-w-0 tw-flex tw-flex-nowrap tw-gap-1.5 tw-overflow-x-auto tw-px-1 -tw-mx-1'
            style={{
              maskImage: "linear-gradient(to right, transparent, black 20px, black calc(100% - 20px), transparent)",
              WebkitMaskImage:
                "linear-gradient(to right, transparent, black 20px, black calc(100% - 20px), transparent)",
            }}
          >
            {suggestions.map(s => {
              const chipClassName =
                "tw-shrink-0 tw-cursor-pointer tw-rounded-full tw-px-4 tw-py-2 tw-whitespace-nowrap tw-bg-cream tw-text-black tw-type-xs hover:tw-bg-[#EDEBE8] tw-transition-colors tw-outline-none focus-visible:tw-outline-focus";

              if (s.href) {
                return (
                  <BaseLink
                    key={s.id}
                    href={s.href}
                    aria-label={s.ariaLabel ?? s.label}
                    className={chipClassName}
                    onClick={
                      s.onSelect
                        ? e => {
                            // Let modifier clicks (cmd+click, ctrl+click, middle-click) open a new tab normally.
                            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
                              s.onSelect!(true);
                              return;
                            }
                            e.preventDefault();
                            s.onSelect!(false);
                          }
                        : undefined
                    }
                  >
                    {s.label}
                  </BaseLink>
                );
              }

              return (
                <button
                  key={s.id}
                  type='button'
                  onClick={() => {
                    comboBoxProps.onInputChange(s.label);
                    s.onSelect?.(false);
                  }}
                  aria-label={s.ariaLabel ?? s.label}
                  className={chipClassName}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

SearchInput.displayName = "SearchInput";

export { SearchInput };
export type { SearchInputProps, SearchSuggestion };
